import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  AccountBalance,
  Description,
  Public,
  Notifications,
  Category,
  ExtensionOutlined,
  CheckCircleOutline,
  ErrorOutline,
  TrendingUp,
  ListAlt,
  Timeline,
  AdminPanelSettings,
  ShieldOutlined,
  CreditCard,
  Widgets,
  PersonOutline,
  AccountTree,
  Place,
  Map,
  RoomOutlined,
  CalendarToday,
  MonetizationOn,
  ShowChart,
  Schedule,
  Group,
  BarChart,
  Security,
  AccountBalanceWallet,
  Autorenew,
  ReportProblem,
  WhatsApp,
} from '@mui/icons-material';

export interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  roles?: string[];
  permission?: string; // Permission required to see this menu item
}

// Super Admin Menu Items - with granular permissions
export const superAdminMenuItems: MenuItem[] = [
  {
    title: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/superadmin/dashboard',
    permission: 'sa.dashboard.view',
  },
  {
    title: 'Counties',
    icon: <Public />,
    permission: 'sa.counties.view',
    children: [
      { title: 'View Counties', icon: <Map />, path: '/superadmin/counties', permission: 'sa.counties.view' },
      { title: 'County Users', icon: <PersonOutline />, path: '/superadmin/county-users', permission: 'sa.county_users.view' },
      { title: 'Touch Points', icon: <RoomOutlined />, path: '/superadmin/touch-points', permission: 'sa.touch_points.view' },
    ],
  },
  {
    title: 'Modules',
    icon: <ExtensionOutlined />,
    permission: 'sa.modules.view',
    children: [
      { title: 'Modules', icon: <Widgets />, path: '/superadmin/modules', permission: 'sa.modules.view' },
      { title: 'Schedules', icon: <Schedule />, path: '/superadmin/schedules', permission: 'sa.schedules.view' },
      { title: 'Parts', icon: <AccountTree />, path: '/superadmin/parts', permission: 'sa.parts.view' },
      { title: 'Categories', icon: <Category />, path: '/superadmin/categories', permission: 'sa.categories.view' },
      { title: 'Services', icon: <BusinessIcon />, path: '/superadmin/services', permission: 'sa.services.view' },
    ],
  },
  {
    title: 'Transactions',
    icon: <ReceiptIcon />,
    permission: 'sa.transactions.view',
    children: [
      { title: 'All Transactions', icon: <ListAlt />, path: '/superadmin/transactions', permission: 'sa.transactions.view' },
      { title: 'Successful', icon: <CheckCircleOutline />, path: '/superadmin/transactions/successful', permission: 'sa.transactions.view' },
      { title: 'Failed', icon: <ErrorOutline />, path: '/superadmin/transactions/failed', permission: 'sa.transactions.view' },
    ],
  },
  {
    title: 'Reported Issues',
    icon: <ReportProblem />,
    path: '/superadmin/issues',
    permission: 'sa.transactions.view',
  },
  {
    title: 'Reports',
    icon: <AssessmentIcon />,
    permission: 'sa.reports.revenue',
    children: [
      { title: 'Revenue Report', icon: <MonetizationOn />, path: '/superadmin/reports/revenue', permission: 'sa.reports.revenue' },
      { title: 'Transactions Report', icon: <TrendingUp />, path: '/superadmin/reports/transactions', permission: 'sa.reports.transactions' },
      { title: 'Service Usage', icon: <ShowChart />, path: '/superadmin/reports/service-usage', permission: 'sa.reports.service_usage' },
      { title: 'Billing Report', icon: <ReceiptIcon />, path: '/superadmin/reports/billing', permission: 'sa.counties.view' },
      { title: 'Audit Trail', icon: <Timeline />, path: '/superadmin/reports/audit-trail', permission: 'sa.reports.audit_trail' },
    ],
  },
  {
    title: 'User Management',
    icon: <AdminPanelSettings />,
    permission: 'sa.users.view',
    children: [
      { title: 'Users', icon: <PeopleIcon />, path: '/superadmin/users', permission: 'sa.users.view' },
      { title: 'Roles', icon: <ShieldOutlined />, path: '/superadmin/roles', permission: 'sa.roles.view' },
      { title: 'Permissions', icon: <Security />, path: '/superadmin/permissions', permission: 'sa.permissions.view' },
    ],
  },
  {
    title: 'Configurations',
    icon: <SettingsIcon />,
    permission: 'sa.config.payment_modules',
    children: [
      { title: 'Payment Modules', icon: <CreditCard />, path: '/superadmin/payment-modules', permission: 'sa.config.payment_modules' },
      { title: 'Module Types', icon: <Widgets />, path: '/superadmin/module-types', permission: 'sa.config.module_types' },
    ],
  },
  {
    title: 'Notifications',
    icon: <Notifications />,
    path: '/superadmin/notifications',
    permission: 'sa.notifications.view',
  },
  {
    title: 'WhatsApp Ops',
    icon: <WhatsApp />,
    path: '/superadmin/whatsapp',
    permission: 'sa.counties.view',
  },
];

// County Admin Menu Items - now with granular permissions
export const countyAdminMenuItems: MenuItem[] = [
  {
    title: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    permission: 'dashboard.view',
  },
  {
    title: 'Modules',
    icon: <ExtensionOutlined />,
    path: '/modules',
    permission: 'modules.view',
  },
  {
    title: 'Service Applications',
    icon: <Description />,
    path: '/applications',
    permission: 'applications.view',
  },
  {
    title: 'Transactions',
    icon: <ReceiptIcon />,
    path: '/transactions',
    permission: 'transactions.view',
  },
  {
    title: 'Customers',
    icon: <Group />,
    children: [
      { title: 'Individuals', icon: <PersonOutline />, path: '/customers/individuals', permission: 'customers.view' },
      { title: 'Businesses', icon: <BusinessIcon />, path: '/customers/businesses', permission: 'customers.view' },
      { title: 'Subscriptions', icon: <Autorenew />, path: '/customers/subscriptions', permission: 'customers.view' },
      { title: 'Reported Issues', icon: <ReportProblem />, path: '/issues', permission: 'customers.view' },
    ],
    permission: 'customers.view',
  },
  {
    title: 'Finance & Budget',
    icon: <AccountBalance />,
    children: [
      { title: 'Dashboard', icon: <ShowChart />, path: '/finance/dashboard', permission: 'finance.view' },
      { title: 'Financial Periods', icon: <CalendarToday />, path: '/financial-periods', permission: 'finance.view' },
      { title: 'Performance Reports', icon: <BarChart />, path: '/finance/reports', permission: 'finance.view_reports' },
    ],
    permission: 'finance.view',
  },
  {
    title: 'Reports',
    icon: <AssessmentIcon />,
    children: [
      { title: 'Revenue Report', icon: <MonetizationOn />, path: '/reports/revenue', permission: 'reports.revenue' },
      { title: 'Wallet Report', icon: <AccountBalanceWallet />, path: '/reports/wallet', permission: 'reports.revenue' },
      { title: 'Transactions Report', icon: <TrendingUp />, path: '/reports/transactions', permission: 'reports.transactions' },
      { title: 'Service Usage', icon: <ShowChart />, path: '/reports/service-usage', permission: 'reports.service_usage' },
      { title: 'Audit Trail', icon: <Timeline />, path: '/reports/audit-trail', permission: 'reports.audit_trail' },
    ],
    permission: 'reports.revenue',
  },
  {
    title: 'User Management',
    icon: <AdminPanelSettings />,
    children: [
      { title: 'Users', icon: <PeopleIcon />, path: '/users', permission: 'users.view' },
      { title: 'Roles', icon: <ShieldOutlined />, path: '/roles', permission: 'roles.view' },
      { title: 'Permissions', icon: <Security />, path: '/permissions', permission: 'permissions.view' },
    ],
    permission: 'users.view',
  },
  {
    title: 'Configurations',
    icon: <SettingsIcon />,
    children: [
      { title: 'County Profile', icon: <AccountBalance />, path: '/county/profile', permission: 'profile.view' },
      { title: 'Sub-Counties', icon: <Map />, path: '/subcounties', permission: 'locations.view' },
      { title: 'Wards', icon: <Place />, path: '/wards', permission: 'locations.view' },
      { title: 'Zones', icon: <RoomOutlined />, path: '/zones', permission: 'locations.view' },
      { title: 'Payment Configurations', icon: <CreditCard />, path: '/payment-config', permission: 'payment_config.view' },
      { title: 'Module Templates', icon: <Widgets />, path: '/module-templates', permission: 'module_templates.view' },
    ],
    permission: 'profile.view',
  },
  {
    title: 'Notifications',
    icon: <Notifications />,
    path: '/notifications',
    permission: 'notifications.view',
  },
];

// Map old permission names to new ones for backward compatibility
const permissionAliases: Record<string, string[]> = {
  'dashboard.view': ['view_county_data', 'dashboard.view'],
  'modules.view': ['manage_county_services', 'modules.view'],
  'services.view': ['manage_county_services', 'services.view'],
  'applications.view': ['manage_county_services', 'applications.view'],
  'transactions.view': ['view_county_transactions', 'manage_county_transactions', 'transactions.view'],
  'customers.view': ['view_county_data', 'customers.view'],
  'finance.view': ['view_county_reports', 'finance.view'],
  'finance.view_reports': ['view_county_reports', 'finance.view_reports'],
  'reports.revenue': ['view_county_reports', 'reports.revenue'],
  'reports.transactions': ['view_county_reports', 'reports.transactions'],
  'reports.service_usage': ['view_county_reports', 'reports.service_usage'],
  'reports.audit_trail': ['view_county_audit_trail', 'reports.audit_trail'],
  'users.view': ['manage_county_users', 'users.view'],
  'roles.view': ['manage_county_roles', 'roles.view'],
  'permissions.view': ['manage_county_permissions', 'permissions.view'],
  'profile.view': ['edit_county_data', 'profile.view'],
  'locations.view': ['edit_county_data', 'locations.view'],
  'payment_config.view': ['manage_county_payments', 'payment_config.view'],
  'module_templates.view': ['manage_county_services', 'module_templates.view'],
  'notifications.view': ['view_county_data', 'notifications.view'],
};

// Check if user has a specific permission (supports old and new names)
export const hasPermission = (userPermissions: string[], permission: string): boolean => {
  if (userPermissions.includes(permission)) return true;
  const aliases = permissionAliases[permission];
  if (aliases) return aliases.some(alias => userPermissions.includes(alias));
  return false;
};

// Filter menu items based on user permissions
export const filterMenuByPermissions = (items: MenuItem[], userPermissions: string[]): MenuItem[] => {
  return items
    .filter(item => {
      // If no permission required, show it
      if (!item.permission) return true;
      return hasPermission(userPermissions, item.permission);
    })
    .map(item => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter(child => {
            if (!child.permission) return true;
            return hasPermission(userPermissions, child.permission);
          }),
        };
      }
      return item;
    })
    .filter(item => !item.children || item.children.length > 0); // Remove empty parent menus
};

// Helper function to filter menu items based on user type and roles/permissions
export const getMenuItemsByRole = (userRoles: string[], userType?: string, userPermissions?: string[]): MenuItem[] => {
  const isAdmin = userRoles.some(r => r.toLowerCase().includes('admin'));

  if (userType === 'superadmin' || userType === 'admin') {
    // Super Admin role gets full access; other superadmin users get filtered
    if (isAdmin) return superAdminMenuItems;
    if (userPermissions && userPermissions.length > 0) {
      return filterMenuByPermissions(superAdminMenuItems, userPermissions);
    }
    return superAdminMenuItems; // fallback: show all if permissions not loaded
  }

  if (userType === 'county') {
    // County Admin role gets full access
    if (isAdmin) return countyAdminMenuItems;
    // Non-admin county users: filter by permissions (always — no fallback to all)
    return filterMenuByPermissions(countyAdminMenuItems, userPermissions || []);
  }

  return [];
};
